import { marked } from 'marked';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../api';
import ModalContext from '../../contexts/ModalContext';
import { useDefaultToast } from '../../hooks/useDefaultToast';
import { buildGroupProps } from '../../utils/elementisUtils';

import Button from '@pelckmans/elementis/components/button';
import Checkbox from '@pelckmans/elementis/components/checkbox';
import Dialog from '@pelckmans/elementis/components/dialog';

export type MarketingNotification = {
  id: string;
  teacherOnly: boolean;
  text: string;
  title: string;
  validFrom: string;
  validTo: string;
  visualId?: string;
  visualUrl?: string;
  moreInfo: {
    visible: boolean;
    label?: string;
    url?: string;
    urlExternal?: boolean;
  };
};

type MarketingNotificationModalProps = { id: string; notification: MarketingNotification };

const MarketingNotificationModal = ({ id, notification }: MarketingNotificationModalProps) => {
  const { t } = useTranslation();
  const { closeModal } = useContext(ModalContext);
  const [closePermanently, setClosePermanently] = useState(false);
  const { failedToast } = useDefaultToast();

  const onClose = async () => {
    try {
      await api.put(`/studio/user/marketing-notifications/${notification.id}/seen`, { permanent: closePermanently });
    } catch (err) {
      failedToast();
    }
    closeModal(id);
  };

  return (
    <Dialog
      className="marketing-notification"
      id={id}
      aria-label={t('SHARED_COMPONENTS.MARKETING_NOTIFICATION_MODAL.ARIA_LABEL')}
      modal
      onClose={onClose}
    >
      <Dialog.Header title={notification.title} onClose={onClose} aria-label={t('ACTIONS.CLOSE')} />
      <Dialog.Body>
        {notification.visualUrl && (
          <img
            className="visual"
            src={`${process.env.REACT_APP_P_API_URL}${notification.visualUrl}`}
            alt={notification.title}
          />
        )}
        <div className="rich-text" dangerouslySetInnerHTML={{ __html: marked.parse(notification.text) }} />
        {notification.moreInfo?.visible && (
          <Button
            className="more-info-button"
            prominence="primary"
            as="link"
            href={notification.moreInfo.url!}
            target={notification.moreInfo.urlExternal ? '_blank' : '_self'}
          >
            {notification.moreInfo.label || t('SHARED_COMPONENTS.MARKETING_NOTIFICATION_MODAL.SHOW_MORE')}
          </Button>
        )}
      </Dialog.Body>
      <Dialog.Footer
        buttonGroupPropsLeft={buildGroupProps(
          <Checkbox onChange={() => setClosePermanently(!closePermanently)} checked={closePermanently}>
            {t('SHARED_COMPONENTS.MARKETING_NOTIFICATION_MODAL.NEVER_SHOW_AGAIN')}
          </Checkbox>,
        )}
        buttonGroupPropsRight={buildGroupProps(
          <Button prominence="neutral" onClick={onClose}>
            {t('ACTIONS.CLOSE')}
          </Button>,
        )}
      />
    </Dialog>
  );
};

export default MarketingNotificationModal;
